import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { toast } from 'sonner';

import { packagingApi } from 'api/packaging';
import { PackagingPOCreateItemsCard } from 'components/packaging/components/packagingPOV2/create/PackagingPOCreateItemsCard';
import { PackagingPOCreatePageHeader } from 'components/packaging/components/packagingPOV2/create/PackagingPOCreatePageHeader';
import { PackagingPOCreatePrimaryCard } from 'components/packaging/components/packagingPOV2/create/PackagingPOCreatePrimaryCard';
import { usePackagingPurchaseOrderItems } from 'components/packaging/components/packagingPOV2/entityView/PackagingPurchaseOrderQueries';
import { usePackagingPurchaseOrdersV2Search } from 'components/packaging/components/packagingPOV2/queries/PackagingPOQueries';
import { usePackagingPOBackHref } from 'components/packaging/components/packagingPOV2/usePackagingPOBackHref';
import {
    extractFacilityIds,
    validateCreatePackagingPO,
} from 'components/packaging/components/packagingPOV2/utils/packagingPOValidation';

const DEFAULT_DRAFT = {
    vendorId: '',
    facilities: [],
    scheduledDeliveryDate: '',
    scheduledTime: '',
    referenceNumber: '',
};

const createDuplicateItemUid = (index) => `dup-${Date.now()}-${index}-${Math.random().toString(16).slice(2)}`;

const toItemUnitPrice = (rawValue) => {
    if (rawValue === null || rawValue === undefined || rawValue === '') return '';
    const numericValue = Number(rawValue);
    if (Number.isFinite(numericValue)) {
        return numericValue.toFixed(3);
    }
    return String(rawValue);
};

const toStringOrEmpty = (value) => {
    if (value === null || value === undefined) return '';
    return String(value);
};

const getDuplicateNumPallets = (item) => {
    if (item?.numPallets != null) return toStringOrEmpty(item.numPallets);
    return '';
};

const getDuplicateUnitsPerCase = (item) => {
    if (item?.unitsPerCase != null) return toStringOrEmpty(item.unitsPerCase);
    if (item?.model?.unitsPerCase != null) return toStringOrEmpty(item.model.unitsPerCase);
    return '';
};

const buildDuplicateDraft = (sourcePO, sourcePOEntity) => {
    if (!sourcePO && !sourcePOEntity) {
        return DEFAULT_DRAFT;
    }

    return {
        ...DEFAULT_DRAFT,
        vendorId:
            sourcePOEntity?.vendorID ?? sourcePOEntity?.vendorId ?? sourcePO?.vendorID ?? sourcePO?.vendorId ?? '',
        facilities: [],
        scheduledDeliveryDate: '',
        scheduledTime: '',
        referenceNumber:
            sourcePOEntity?.referenceNumber ??
            sourcePOEntity?.reference ??
            sourcePO?.referenceNumber ??
            sourcePO?.reference ??
            '',
    };
};

const buildDuplicateItems = (sourcePODetail) => {
    const sourceItems = sourcePODetail?.items ?? [];
    if (!Array.isArray(sourceItems) || sourceItems.length === 0) return [];

    return sourceItems
        .map((item, index) => {
            const itemId = Number(item?.id);
            if (!Number.isFinite(itemId) || itemId <= 0) {
                return null;
            }

            return {
                uid: createDuplicateItemUid(index),
                id: itemId,
                masterSku: item?.masterSku ?? item?.masterSKU ?? '',
                description: item?.description ?? item?.itemName ?? '',
                notes: item?.notes ?? '',
                numPallets: getDuplicateNumPallets(item),
                qty: item?.qty != null ? String(item.qty) : '',
                unitPrice: toItemUnitPrice(item?.unitPrice),
                unitsPerCase: getDuplicateUnitsPerCase(item),
                unitsPerCaseUOM: item?.unitsPerCaseUOM ?? 'ct',
                vendorSku: item?.vendorSku ?? '',
            };
        })
        .filter(Boolean);
};

export function CreatePackagingPOV2() {
    const history = useHistory();
    const location = useLocation();

    const [draft, setDraft] = useState(DEFAULT_DRAFT);
    const [items, setItems] = useState([]);
    const [hasAttemptedSubmit, setHasAttemptedSubmit] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const isSubmittingRef = useRef(false);
    const hasInitializedDuplicateRef = useRef(false);

    const searchParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
    const duplicateFromId = searchParams.get('duplicateFrom');
    const { data: sourcePOSearch, isLoading: isLoadingSourcePO } = usePackagingPurchaseOrdersV2Search(
        { poNumber: duplicateFromId, pageSize: 1 },
        { enabled: Boolean(duplicateFromId) },
    );
    const sourcePO = sourcePOSearch?.rows?.[0] ?? null;
    const { data: sourcePODetail, isLoading: isLoadingSourcePODetail } = usePackagingPurchaseOrderItems({
        id: duplicateFromId,
        enabled: Boolean(duplicateFromId),
    });
    const isDuplicateLoading = Boolean(duplicateFromId) && (isLoadingSourcePO || isLoadingSourcePODetail);

    useEffect(() => {
        if (!duplicateFromId) return;
        if (isLoadingSourcePO || isLoadingSourcePODetail) return;
        if (!sourcePO && !sourcePODetail) return;
        if (hasInitializedDuplicateRef.current) return;

        setDraft(buildDuplicateDraft(sourcePO, sourcePODetail));
        setItems(buildDuplicateItems(sourcePODetail));
        hasInitializedDuplicateRef.current = true;
    }, [duplicateFromId, sourcePO, sourcePODetail, isLoadingSourcePO, isLoadingSourcePODetail]);

    const validation = useMemo(() => validateCreatePackagingPO({ draft, items }), [draft, items]);
    const backHref = usePackagingPOBackHref();

    const handleSave = useCallback(async () => {
        if (isSubmittingRef.current) return;
        isSubmittingRef.current = true;

        setHasAttemptedSubmit(true);
        if (!validation.isValid) {
            isSubmittingRef.current = false;
            return;
        }

        setIsSubmitting(true);
        const createdFacilityIds = [];
        const facilityIds = extractFacilityIds(draft.facilities);
        const facilityNamesById = new Map(
            (Array.isArray(draft.facilities) ? draft.facilities : [])
                .map((facility) => {
                    if (facility == null) return null;
                    if (typeof facility === 'object' && facility.id != null) {
                        return [Number(facility.id), facility.name ?? facility.label ?? null];
                    }
                    return [Number(facility), null];
                })
                .filter(Boolean),
        );
        try {
            const baseParams = {
                vendorID: Number(draft.vendorId),
                deliverDate: draft.scheduledDeliveryDate,
                deliverTime: draft.scheduledTime,
                referenceNumber: draft.referenceNumber,
                items: validation.submitItems,
            };

            const results = await Promise.all(
                facilityIds.map(async (facilityId) => {
                    try {
                        const response = await packagingApi.createPurchaseOrder({
                            ...baseParams,
                            facilityID: facilityId,
                        });
                        return { facilityId, response };
                    } catch (error) {
                        return { facilityId, error };
                    }
                }),
            );

            const failures = [];
            results.forEach((result) => {
                if (result.error) {
                    failures.push({
                        facilityId: result.facilityId,
                        message: result.error?.message ?? null,
                    });
                    return;
                }
                if (result.response?.status === false) {
                    failures.push({
                        facilityId: result.facilityId,
                        message: result.response?.msg ?? null,
                    });
                    return;
                }
                createdFacilityIds.push(result.facilityId);
            });

            if (failures.length > 0) {
                if (createdFacilityIds.length > 0) {
                    const createdLabels = createdFacilityIds
                        .map((id) => facilityNamesById.get(id) || `Facility ${id}`)
                        .join(', ');
                    toast.success(`Created purchase orders for: ${createdLabels}`);
                }
                const failureSummary = failures
                    .map(({ facilityId, message }) => {
                        const label = facilityNamesById.get(facilityId) || `Facility ${facilityId}`;
                        return message ? `${label} (${message})` : label;
                    })
                    .join(', ');
                toast.error(`Failed to create purchase orders for: ${failureSummary}`);
                return;
            }

            toast.success('Purchase Order created!');
            history.push(backHref);
        } catch (error) {
            if (createdFacilityIds.length > 0) {
                const createdLabels = createdFacilityIds.map((id) => `Facility ${id}`).join(', ');
                toast.success(`Created purchase orders for: ${createdLabels}`);
            }
            toast.error(error?.message ?? 'Error creating purchase order');
        } finally {
            isSubmittingRef.current = false;
            setIsSubmitting(false);
        }
    }, [backHref, draft, history, validation.isValid, validation.submitItems]);

    return (
        <div className="flex min-h-screen flex-col gap-6 px-4 py-4">
            <PackagingPOCreatePageHeader
                title="Create Packaging Purchase Order"
                statusLabel="Creating"
                onBack={() => history.push(backHref)}
                onCancel={() => history.push(backHref)}
                onSave={handleSave}
                isDisabled={isSubmitting}
                isLoadingDuplicate={isDuplicateLoading}
            />

            <PackagingPOCreatePrimaryCard
                draft={draft}
                onDraftChange={setDraft}
                showValidation={hasAttemptedSubmit}
                primaryErrors={validation.primaryErrors}
                primaryFieldErrors={validation.primaryFieldErrors}
            />

            <PackagingPOCreateItemsCard
                items={items}
                onItemsChange={setItems}
                showValidation={hasAttemptedSubmit}
                itemErrors={validation.itemErrors}
            />
        </div>
    );
}

CreatePackagingPOV2.displayName = 'CreatePackagingPOV2';
